import * as z from 'zod';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { Filter } from '@ais-ui/websockets';
import { SelectType } from '@ais-ui/ui-core';
import type { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import {
  timeFormatHoursMinutesSecondsSchema,
  checkDirectionAngleSchema,
} from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { mutualDetectorFormSchema } from '../Mutations/detectorForm';
import { convertActivityFilters } from '../detectorUtils';

// export const useActivityFilters = (): SelectType[] => {
//   const { messages } = useLocalization();
//
//   return [
//     {
//       value: 'name',
//       label: 'Name change',
//       help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipNameChange,
//     },
//     {
//       value: 'destination',
//       label: 'Destination change',
//       help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipDestinationChange,
//     },
//     {
//       value: 'draught',
//       label: 'Draught change',
//       help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipDraughtChange,
//     },
//     {
//       value: 'eta',
//       label: 'Estimated time of arrival change',
//       help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipETAChange,
//     },
//     {
//       value: 'nav_stat',
//       label: 'Navigational status change',
//       help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipNavStatusChange,
//     },
//     {
//       value: 'fishing',
//       label: 'Fishing',
//       help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipFishingChange,
//     },
//     {
//       value: 'transponder_off',
//       label: 'Transponder off',
//       help: messages.eventdetectorsSaveAdvancedActivityFiltersTooltipTransponderOff,
//     },
//   ];
// };

export const fieldToAreaAccordionMap: {
  [key in keyof AreaDetectorFormValues]: string;
} = {
  entireVesselInside: 'advanced-item',
  name: 'detector-item',
  enabled: 'detector-item',
  selectedGroups: 'detector-item',
  severity: 'detector-item',
  filterId: 'detector-item',
  maintainShiplist: 'detector-item',
  acknowledgeRequired: 'detector-item',
  timeBeforeActivation: 'advanced-item',
  timeBeforeDeactivation: 'advanced-item',
  timeBetweenTrigger: 'advanced-item',
  timeoutWhenActive: 'advanced-item',
  activityFilters: 'advanced-item',
  sendShipNotification: 'advanced-item',
  shipNotificationText: 'advanced-item',
  useVesselDimensions: 'advanced-item',
  navigationStatusTriggerThreshold: 'advanced-item',
  navigationStatusIgnoreThreshold: 'advanced-item',
  driftingSpeedOverGroundThreshold: 'advanced-item',
  driftingAngleThreshold: 'advanced-item',
  driftingTimeThreshold: 'advanced-item',
  exitCheckThreshold: 'advanced-item',
  exitCheckTriggerLost: 'advanced-item',
  deltaSpeedOverGroundThreshold: 'advanced-item',
  transponderOffDuration: 'advanced-item',
  emails: 'detector-item',
  phones: 'detector-item',
  circleCenterCpa: 'advanced-item',
  checkCpa: 'advanced-item',
  cpaLimit: 'advanced-item',
  tcpaLimit: 'advanced-item',
};

export const areaDetectorFormSchema = mutualDetectorFormSchema
  .extend({
    activationText: z.string().optional(),
    deactivationText: z.string().optional(),
    directionMinAngle: checkDirectionAngleSchema.optional(),
    directionMaxAngle: checkDirectionAngleSchema.optional(),
    timeBeforeActivation: timeFormatHoursMinutesSecondsSchema.optional(),
    timeBeforeDeactivation: timeFormatHoursMinutesSecondsSchema.optional(),
    timeBetweenTrigger: timeFormatHoursMinutesSecondsSchema.optional(),
    timeoutWhenActive: timeFormatHoursMinutesSecondsSchema.optional(),
    sendShipNotification: z.boolean(),
    shipNotificationText: z.string().optional(),
    activityFilters: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    ),
    transponderOffDuration: timeFormatHoursMinutesSecondsSchema.optional(),
    navigationStatusTriggerThreshold: z.number().optional(),
    navigationStatusIgnoreThreshold: z.number().optional(),
    driftingSpeedOverGroundThreshold: z.number().optional(),
    driftingAngleThreshold: z.number().optional(),
    driftingTimeThreshold: z.number().optional(),
    exitCheckThreshold: z.number().optional(),
    exitCheckTriggerLost: z.boolean().optional(),
    deltaSpeedOverGroundThreshold: z.number().optional(),
    useVesselDimensions: z.boolean(),
    entireVesselInside: z.boolean(),
    circleCenterCpa: z.boolean().optional(),
    checkCpa: z.boolean().optional(),
    cpaLimit: z.number().optional(),
    tcpaLimit: z.number().optional(),
  })
  .refine((data) => Boolean(data.shapeId) || Boolean(data.shape), {
    message: 'Shape must be provided',
    path: ['shapeId', 'shape'],
  })
  .refine(
    (data) => {
      const hasTransponderOff = data.activityFilters?.some(
        (filter) => filter.value === 'transponder_off'
      );
      return (
        !hasTransponderOff ||
        (hasTransponderOff && Boolean(data.transponderOffDuration))
      );
    },
    {
      message: 'Duration is required when transponder off is selected',
      path: ['transponderOffDuration'],
    }
  );
export type AreaDetectorFormValues = z.infer<typeof areaDetectorFormSchema>;

export const convertFormValuesToBackend = (
  detectorType: string,
  data: AreaDetectorFormValues,
  shape: GeoJson,
  filter: Filter | undefined
): EventdetectorDefinition => {
  const activityFilter = convertActivityFilters(
    data.activityFilters as SelectType[],
    data.transponderOffDuration
  );

  let checkDirection = null;
  if (data.directionMinAngle && data.directionMaxAngle) {
    checkDirection = {
      minAngle: data.directionMinAngle,
      maxAngle: data.directionMaxAngle,
    };
  }

  const notifications = [];
  for (const email of data.emails) {
    notifications.push({
      who: email,
      action: 'email',
    });
  }

  for (const phone of data.phones) {
    notifications.push({
      who: phone,
      action: 'sms',
    });
  }

  return {
    name: data.name,
    type: detectorType,
    acl: data.selectedGroups.map((group) => group.value),
    id: null,
    enabled: data.enabled,
    activityFilter: activityFilter,
    advancedConfiguration: true,
    checkDirection: checkDirection,
    useVesselDimensions: data.useVesselDimensions as boolean,
    entireVessel: data.entireVesselInside as boolean,
    sendShipNotification: data.sendShipNotification as boolean,
    shipNotificationText: data.shipNotificationText,
    timeBeforeActivation: data.timeBeforeActivation
      ? data.timeBeforeActivation
      : '',
    timeBeforeDeactivation: data.timeBeforeDeactivation
      ? data.timeBeforeDeactivation
      : '',
    activationText: data.activationText ? data.activationText : '',
    deactivationText: data.deactivationText ? data.deactivationText : '',
    timeBetweenTriggers: data.timeBetweenTrigger ? data.timeBetweenTrigger : '',
    timeoutWhenActive: data.timeoutWhenActive ? data.timeoutWhenActive : '',
    severity: data.severity,
    shape: data.shapeId,
    geometry: shape,
    filterId: filter?.uuid ? filter.uuid : null,
    filterString: filter?.filter ? filter?.filter : '',
    notify: notifications,
    notifyOnActivation: true,
    notifyOnDeactivation: true,
    ackRequired: data.acknowledgeRequired,
    shiplist: {
      active: data.maintainShiplist,
    },
    circleCenterCpa: data.circleCenterCpa,
    checkCpa: data.checkCpa,
    cpaLimit: data.cpaLimit,
    tcpaLimit: data.tcpaLimit,
  };
};
